/**
 * Aba "Lembretes de Agendamento" da Tela A2 (Config Tenant) — ADR-150.
 *
 * Expõe o campo REAL `appointmentReminderPolicy` do `tenant-config` (R15):
 * master switch (opt-in P12 — null/off = zero disparo) + antecedências
 * canônicas (ADR-115 D115.3; defaults por pacote quando nada explícito) +
 * follow-up pós-conclusão + modo de operação. O motor (cascades do
 * AppointmentsService + worker SOA) já existe; esta aba dá ao admin a config
 * por-tenant. Escrita gated em `tenant-config-manage`.
 *
 * Resolução efetiva única BE+FE: `resolveAppointmentReminderPolicy` do
 * @lume/shared — a UI mostra exatamente o que o runtime fará.
 *
 * Admin Hub T10 — os botões locais Descartar/Salvar saíram; a seção registra
 * na `StickySaveBar` do domínio via `useSectionSave` (mesmo movimento do
 * `GeneralTab`, T7, e do `OperatorShiftTab`, T9). ⚠️ Grava agora com
 * `update.mutateAsync` (não `update.mutate`). Cabeçalho próprio também saiu —
 * dentro de uma `DomainSection` o título/descrição já vêm do catálogo
 * (item.label/item.efeito; o efeito de reagendar/cancelar do header antigo
 * foi dobrado em `catalog.ts` — vide `catalog.spec.ts`).
 *
 * ★ RULING do coordenador (T10 dispatch) — NÃO existe (nem se constrói) uma
 * dependência "lembretes desabilitados quando agenda nativa está off". Este
 * componente é (e sempre foi) AGNÓSTICO à origem do agendamento (visita
 * técnica, consulta, entrega — vide o parágrafo abaixo); tenants COM ERP
 * agendam por lá e continuam precisando de lembretes daqui. Ver
 * `AgendaLembretesPage.spec.tsx` para o teste de INDEPENDÊNCIA que assere
 * isso na composição do domínio.
 *
 * Registro NÃO acontece quando `enabled && offsets.length === 0` (mesmo
 * padrão de `hasInvalidRange` em `SilentTab.tsx`, T8): ligar sem nenhuma
 * antecedência é um estado incompleto que o backend rejeitaria — a seção
 * simplesmente sai da barra até o usuário escolher ao menos uma. O aviso
 * `role="alert"` inline continua vivo (honestidade sobre o estado ATUAL do
 * form), sem depender da barra para aparecer.
 */

import {
  DEFAULT_APPOINTMENT_REMINDER_MODE,
  DEFAULT_FOLLOWUP_DELAY_HOURS,
  REMINDER_OFFSETS_BY_PACKAGE,
  resolveAppointmentReminderPolicy,
  type AppointmentReminderMode,
  type FollowupDelayHours,
  type ReminderOffsetHours,
  type TenantPackageCode,
} from '@lume/shared';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useAuthStore } from '../../../stores/auth-store';
import { useSectionSave } from '../hub/save-bar-context';

import { REMINDERS_TAB_FIELD_LABELS } from './config-tenant-labels';
import { useTenantConfig, useUpdateTenantConfig } from './use-tenant-config';

import type { AppointmentReminderPolicyRequest, SerializedTenantConfig } from './types';

const OFFSET_OPTIONS: readonly { value: ReminderOffsetHours; label: string }[] = [
  { value: 168, label: '1 semana antes (D-7)' },
  { value: 72, label: '3 dias antes (D-3)' },
  { value: 24, label: '1 dia antes (D-1)' },
  { value: 2, label: '2 horas antes' },
];

const DELAY_OPTIONS: readonly { value: FollowupDelayHours; label: string }[] = [
  { value: 4, label: '4 horas depois' },
  { value: 24, label: '1 dia depois' },
  { value: 48, label: '2 dias depois' },
];

const MODE_OPTIONS: readonly { value: AppointmentReminderMode; label: string }[] = [
  { value: 'H_b', label: 'IA envia e aguarda (resposta cai no inbox)' },
  { value: 'H_c', label: 'IA envia e conduz a conversa' },
  { value: 'H_a', label: 'Atendente envia (vira pendência na hora do lembrete)' },
];

interface FormState {
  enabled: boolean;
  offsets: ReminderOffsetHours[];
  followupEnabled: boolean;
  followupDelayHours: FollowupDelayHours;
  mode: AppointmentReminderMode;
}

function toForm(c: SerializedTenantConfig, packageType: TenantPackageCode): FormState {
  const effective = resolveAppointmentReminderPolicy(c.appointmentReminderPolicy, packageType);
  if (effective === null) {
    // Desligada — pré-carrega os defaults do pacote para quando o admin ligar.
    return {
      enabled: false,
      offsets: [...REMINDER_OFFSETS_BY_PACKAGE[packageType]],
      followupEnabled: false,
      followupDelayHours: DEFAULT_FOLLOWUP_DELAY_HOURS,
      mode: DEFAULT_APPOINTMENT_REMINDER_MODE,
    };
  }
  return {
    enabled: true,
    offsets: [...effective.reminderOffsetsHours],
    followupEnabled: effective.followupEnabled,
    followupDelayHours: effective.followupDelayHours,
    mode: effective.mode,
  };
}

/** Form → PATCH. Desligado ⇒ `null` (limpa — opt-in P12); ligado ⇒ policy explícita. */
function buildPolicy(form: FormState): AppointmentReminderPolicyRequest | null {
  if (!form.enabled) return null;
  return {
    version: 1,
    enabled: true,
    reminderOffsetsHours: [...form.offsets].sort((a, b) => b - a),
    followupEnabled: form.followupEnabled,
    followupDelayHours: form.followupDelayHours,
    mode: form.mode,
  };
}

export function RemindersTab({ canManage }: { readonly canManage: boolean }): JSX.Element {
  const query = useTenantConfig();
  const update = useUpdateTenantConfig();
  const rawPackage = useAuthStore((s) => s.tenant?.packageType);
  const packageType: TenantPackageCode =
    rawPackage === 'solo' || rawPackage === 'team' || rawPackage === 'regulated'
      ? rawPackage
      : 'professional';
  const [form, setForm] = useState<FormState | null>(null);

  useEffect(() => {
    if (query.data !== undefined) setForm(toForm(query.data, packageType));
  }, [query.data, packageType]);

  const serverForm = useMemo(
    () => (query.data !== undefined ? toForm(query.data, packageType) : null),
    [query.data, packageType],
  );

  const isDirty = useMemo(() => {
    if (form === null || serverForm === null) return false;
    return JSON.stringify(form) !== JSON.stringify(serverForm);
  }, [form, serverForm]);

  // Ligada sem nenhuma antecedência seria affordance falsa (zero lembrete) —
  // também usada para NÃO registrar a seção na StickySaveBar (mesmo padrão de
  // `hasInvalidRange` em `SilentTab.tsx`, T8): o backend rejeitaria; o aviso
  // inline (role="alert", abaixo) já sinaliza o problema sem depender da barra.
  const noOffsets = form !== null && form.enabled && form.offsets.length === 0;

  // Só os 5 campos reais do form que MUDARAM — o PATCH em si sempre carrega o
  // objeto `appointmentReminderPolicy` INTEIRO (mesmo padrão de
  // `operatorNotificationPolicy` na T9), isto é só para o toast NOMEAR o que
  // o usuário alterou.
  const changedLabels = useMemo(() => {
    if (form === null || serverForm === null) return [];
    const labels: string[] = [];
    if (form.enabled !== serverForm.enabled) labels.push(REMINDERS_TAB_FIELD_LABELS.enabled);
    if (JSON.stringify(form.offsets) !== JSON.stringify(serverForm.offsets)) {
      labels.push(REMINDERS_TAB_FIELD_LABELS.offsets);
    }
    if (form.followupEnabled !== serverForm.followupEnabled) {
      labels.push(REMINDERS_TAB_FIELD_LABELS.followupEnabled);
    }
    if (form.followupDelayHours !== serverForm.followupDelayHours) {
      labels.push(REMINDERS_TAB_FIELD_LABELS.followupDelayHours);
    }
    if (form.mode !== serverForm.mode) labels.push(REMINDERS_TAB_FIELD_LABELS.mode);
    return labels;
  }, [form, serverForm]);

  const previousValues = useRef<{
    appointmentReminderPolicy: AppointmentReminderPolicyRequest | null;
  } | null>(null);

  const handleDiscard = (): void => {
    if (serverForm !== null) setForm(serverForm);
    update.reset();
  };

  // Precisa ser chamado incondicionalmente em todo render (regra dos hooks) —
  // antes dos returns condicionais de loading/erro abaixo.
  useSectionSave(
    isDirty && canManage && !noOffsets && form !== null && serverForm !== null
      ? {
          sectionId: 'lembretes',
          changedLabels,
          save: async () => {
            previousValues.current = { appointmentReminderPolicy: buildPolicy(serverForm) };
            await update.mutateAsync({ appointmentReminderPolicy: buildPolicy(form) });
          },
          discard: handleDiscard,
          undo: async () => {
            if (previousValues.current !== null) {
              await update.mutateAsync(previousValues.current);
            }
          },
        }
      : null,
  );

  if (query.isLoading) {
    return <p className="text-sm text-text-secondary">Carregando configurações…</p>;
  }
  if (query.isError || query.data === undefined || form === null) {
    return <p className="text-sm text-red-600">Falha ao carregar as configurações do tenant.</p>;
  }

  const fieldsDisabled = !canManage || update.isPending;

  const toggleOffset = (value: ReminderOffsetHours, checked: boolean): void => {
    setForm((f) =>
      f === null
        ? f
        : {
            ...f,
            offsets: checked ? [...f.offsets, value] : f.offsets.filter((o) => o !== value),
          },
    );
  };

  return (
    <div className="max-w-2xl">
      {/* Header próprio saiu (Admin Hub T10) — DomainSection já supre título
          (item.label) + descrição (item.efeito, agora com o efeito de
          reagendar/cancelar dobrado — catalog.ts). */}
      <div className="space-y-6">
        {/* Master switch (opt-in P12) */}
        <div className="flex items-center justify-between rounded-xl border border-border-subtle bg-surface p-4">
          <div>
            <h3 className="text-sm font-medium text-text-primary">Lembretes ativos</h3>
            <p className="mt-0.5 text-xs text-text-secondary">
              Desligado, nenhum lembrete ou follow-up de agendamento é enviado para este tenant.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={form.enabled}
            aria-label="Lembretes de agendamento ativos"
            disabled={fieldsDisabled}
            onClick={() => {
              setForm((f) => (f === null ? f : { ...f, enabled: !f.enabled }));
            }}
            className={`relative h-5 w-9 flex-shrink-0 rounded-full transition-colors disabled:opacity-50 ${
              form.enabled ? 'bg-accent' : 'bg-border-subtle'
            }`}
          >
            <span
              className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${
                form.enabled ? 'translate-x-[18px]' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>

        {/* Antecedências */}
        <div
          className={`rounded-xl border border-border-subtle bg-surface p-4 ${
            form.enabled ? '' : 'opacity-50'
          }`}
        >
          <h3 className="text-sm font-medium text-text-primary">Antecedências</h3>
          <p className="mt-1 mb-3 text-xs text-text-secondary">
            Lembretes muito distantes do horário não são criados (visita amanhã não recebe D-7).
          </p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {OFFSET_OPTIONS.map(({ value, label }) => (
              <label
                key={value}
                className={`flex items-center gap-2 rounded-lg border border-border-subtle px-3 py-2 text-sm ${
                  fieldsDisabled || !form.enabled
                    ? 'cursor-not-allowed text-text-secondary'
                    : 'cursor-pointer text-text-primary hover:bg-app-bg'
                }`}
              >
                <input
                  type="checkbox"
                  checked={form.offsets.includes(value)}
                  disabled={fieldsDisabled || !form.enabled}
                  onChange={(e) => {
                    toggleOffset(value, e.target.checked);
                  }}
                  className="h-4 w-4 rounded border-border-subtle text-accent focus:ring-accent disabled:opacity-50"
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
          {noOffsets && (
            <p role="alert" className="mt-2 text-xs text-red-600">
              Escolha ao menos uma antecedência — ligado sem antecedência nenhum lembrete sai.
            </p>
          )}
        </div>

        {/* Follow-up pós-conclusão */}
        <div
          className={`rounded-xl border border-border-subtle bg-surface p-4 ${
            form.enabled ? '' : 'opacity-50'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-text-primary">Follow-up pós-conclusão</h3>
              <p className="mt-0.5 text-xs text-text-secondary">
                Depois que o agendamento é concluído, pergunta ao cliente se correu tudo bem.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={form.followupEnabled}
              aria-label="Follow-up pós-conclusão ativo"
              disabled={fieldsDisabled || !form.enabled}
              onClick={() => {
                setForm((f) => (f === null ? f : { ...f, followupEnabled: !f.followupEnabled }));
              }}
              className={`relative h-5 w-9 flex-shrink-0 rounded-full transition-colors disabled:opacity-50 ${
                form.followupEnabled ? 'bg-accent' : 'bg-border-subtle'
              }`}
            >
              <span
                className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${
                  form.followupEnabled ? 'translate-x-[18px]' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>
          {form.followupEnabled && (
            <label className="mt-3 block">
              <span className="mb-1 block text-xs font-medium text-text-secondary">Quando</span>
              <select
                className="w-full rounded border border-border-subtle bg-surface px-2 py-1.5 text-sm"
                value={form.followupDelayHours}
                disabled={fieldsDisabled || !form.enabled}
                onChange={(e) => {
                  setForm((f) =>
                    f === null
                      ? f
                      : { ...f, followupDelayHours: Number(e.target.value) as FollowupDelayHours },
                  );
                }}
              >
                {DELAY_OPTIONS.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>

        {/* Modo de operação */}
        <div
          className={`rounded-xl border border-border-subtle bg-surface p-4 ${
            form.enabled ? '' : 'opacity-50'
          }`}
        >
          <h3 className="text-sm font-medium text-text-primary">Quem conduz</h3>
          <p className="mt-1 mb-3 text-xs text-text-secondary">
            Como a mensagem é enviada e quem assume a resposta do cliente.
          </p>
          <select
            aria-label="Modo de operação dos lembretes"
            className="w-full rounded border border-border-subtle bg-surface px-2 py-1.5 text-sm"
            value={form.mode}
            disabled={fieldsDisabled || !form.enabled}
            onChange={(e) => {
              setForm((f) =>
                f === null ? f : { ...f, mode: e.target.value as AppointmentReminderMode },
              );
            }}
          >
            {MODE_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Descartar/Salvar saíram — vivem na StickySaveBar do domínio (Admin Hub
          T10); sucesso/erro do save viram toast da barra, não mensagem local.
          Ligado sem antecedência apenas retira a seção da barra (o aviso
          role="alert" acima já sinaliza o problema). */}
      {!canManage && (
        <p className="mt-6 rounded-lg border border-border-subtle bg-app-bg px-4 py-3 text-sm text-text-secondary">
          Você tem acesso de leitura. A edição destas configurações é restrita ao administrador do
          tenant.
        </p>
      )}
    </div>
  );
}
